import React, {
  ReactNode,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { UserProfile } from "../../models/user.model";
import { getUserProfile } from "../../services/user.service";
import { AuthContext } from "../AuthContext";

const INACTIVITY_LIMIT = 10 * 60 * 1000;



export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTimeoutDialog, setShowTimeoutDialog] = useState(false);
  const timeoutIdRef = useRef<number | null>(null);
  const navigate = useNavigate();

  
  const resetInactivityTimeout = useCallback(() => {
     
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = window.setTimeout(() => {
        console.log("Set showTimeoutDialog true");
      setShowTimeoutDialog(true);
    }, INACTIVITY_LIMIT);
  }, []);

  
  const clearInactivityTimeout = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  useEffect(() => {
    const events = ["click", "keydown", "scroll", "mousemove"];
    events.forEach(event => window.addEventListener(event, resetInactivityTimeout));
    resetInactivityTimeout();

    return () => {
      events.forEach(event => window.removeEventListener(event, resetInactivityTimeout));
      clearInactivityTimeout();
    };
  }, [resetInactivityTimeout]);

  
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const storedUser = localStorage.getItem("userProfile");
        if (storedUser) {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }

        const fetchedUser = await getUserProfile();
        setUser(fetchedUser);
        setIsAuthenticated(true);
        localStorage.setItem("userProfile", JSON.stringify(fetchedUser));
      } catch {
        setError("No se pudo cargar la información del usuario.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  
  const login = () => {
    setShowTimeoutDialog(false);
    resetInactivityTimeout();
    setIsAuthenticated(true);
  };

  const logout = () => {
    setShowTimeoutDialog(false);
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem("userProfile");
    clearInactivityTimeout();
    navigate("/");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        user,
        loading,
        setLoading,
        error,
        showTimeoutDialog,
        setShowTimeoutDialog,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

